import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import { debounce, find, get, set, unionBy } from "lodash";
import { useConfirm } from "material-ui-confirm";
import { FC, useEffect, useState } from "react";
import useAsyncEffect from "use-async-effect";
import { HistoryProps } from "../..";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import { providerApi } from "../../../../../../api/providerApi";
import ComponentLoader from "../../../../../../components/ComponentLoader";
import NotesInput from "../../../../../../components/form/NotesInput";
import TypeAheadInput from "../../../../../../components/form/TypeAheadInput/TypeAheadInput";
import CodingSystems from "../../../../../../constants/CodingSystems";
import useComponentLoader from "../../../../../../hooks/useComponentLoader";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import useNotifications from "../../../../../../hooks/useNotifications";
import {
  procedureOutcomeCodes,
  procedureStatusCodes,
} from "../../../../../../slices/options/procedure";
import { Organization } from "../../../../../../types/organization";
import {
  createEncounterParam,
  createReference,
  generateHumanDisplayName,
} from "../../../../../../utils/DataTransformationUtils";
import { createCodeableConcept } from "../../../../../../utils/DataUtils";
import { createSlug } from "../../../../../../utils/createSlug";
import { useDictationCheck } from "../../../../../../hooks/useDictationCheck";
import { emptyHistoryItems } from "../HistoryHelpers";
import DateOptions from "../../../../../../components/form/inputs/DateOptions";

const ENTITY_TYPE = "procedures";

const blankPractitioner = {
  reference: "Practitioner/",
  display: "",
  identifier: "",
};

const blankServiceProviders = {
  display: "",
  identifier: "",
  reference: "",
  type: "Organization",
};

const SurgicalHistoryForm: FC<HistoryProps> = ({
  item,
  patientReference,
  disabled,
  removeHistoryItem,
  setSaved,
  updateHistoryItem,
}) => {
  const confirm = useConfirm();
  const { setNotification } = useNotifications();
  const { isDictationActive } = useDictationCheck();
  const {
    refreshProceduresList,
    encounterReference,
    setEncounterToInProgress,
    refreshPatientHistory,
  } = useMedicalEncounter();
  const [originalSurgicalHistory] = useState<boolean>(
    get(item, "surgicalHistory", true),
  );
  const [intItem, setIntItem] = useState(
    item ?? { ...emptyHistoryItems.surgical(patientReference) },
  );
  const [servProviders, setServProviders] = useState([]);
  const [providerReferences, setProviderReferences] = useState([]);
  const [provider, setProvider] = useState(
    get(intItem, "performer[0].actor[0]", null),
  );
  const [isProviderFreeText, setIsProviderFreeText] = useState(
    item?.performer?.[0]?.actor?.[0]?.identifier?.startsWith("other") || false,
  );

  const [loading, setLoading] = useComponentLoader();
  const [isServiceProvidersFreeText, setIsServiceProvidersFreeText] = useState(
    item?.serviceProvider?.identifier.startsWith("other") || false,
  );

  const loadSurgeons = debounce(async (searchTerm: string) => {
    try {
      setLoading(true);
      const providers = await providerApi.findPractitioners({ q: searchTerm });
      setProviderReferences(
        providers.map((p) =>
          createReference(
            "Practitioner",
            p.id,
            generateHumanDisplayName(p.name[0]),
          ),
        ),
      );
    } catch (error) {
      setNotification({
        message: "Error loading providers",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, 300);

  useAsyncEffect(async () => loadSurgeons(""), []);

  useAsyncEffect(async () => {
    try {
      if (servProviders.length !== 0) return;
      const orgs: Organization[] = await providerApi.findOrganizations({
        "is-hospital": true,
      });
      const orgReferences = orgs
        .map((o) => createReference("Organization", o.id, o.name))
        .sort((a, b) => a?.display?.localeCompare(b?.display));
      if (orgReferences.length > 0) setServProviders(orgReferences);
    } catch (e) {
      console.error(`Error fetching service providers: ${e}`);
    }
  }, [servProviders]);

  const updateItem = (field, value) => {
    const surgery = { ...intItem };
    set(surgery, field, value);
    setIntItem(surgery);
  };

  const onDelete = () => {
    if (!intItem?.id) return;

    confirm({
      title: "Delete Surgical History",
      description:
        "You are about to delete a procedure item for this patient and this action is permanent. Are you sure?",
      confirmationText: "Delete",
      confirmationButtonProps: { color: "error" },
      cancellationText: "Cancel",
      dialogProps: { maxWidth: "sm" },
    }).then(async () => {
      try {
        setLoading(true);
        await deleteSurgicalHistory();
        removeHistoryItem();
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    });
  };

  const deleteSurgicalHistory = async () => {
    try {
      setLoading(true);
      const surgery = { ...intItem, surgicalHistory: true };
      const response = await medicalsApi.updateEntity(
        surgery.id,
        {
          ...surgery,
          status: "entered-in-error",
        },
        ENTITY_TYPE,
        createEncounterParam(encounterReference),
      );
      setIntItem(response);
      updateHistoryItem(response);
      refreshPatientHistory();
    } catch (e) {
      console.error(
        `An error occurred while deleting the patient's surgery history. Please try again. ${e}`,
      );
      setNotification({
        variant: "error",
        message: e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const savePastSurgery = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const surgery = {
        ...intItem,
      };

      if (patientReference) {
        surgery.subject = patientReference;
      }

      if (encounterReference) {
        surgery.encounter = encounterReference;
      }

      let response;
      if (surgery.id) {
        response = await medicalsApi.updateEntity(
          surgery.id,
          surgery,
          ENTITY_TYPE,
          createEncounterParam(encounterReference),
        );
        setIntItem(response);
        if (!intItem.surgicalHistory) refreshProceduresList();
      } else {
        response = await medicalsApi.createEntity(
          surgery,
          ENTITY_TYPE,
          createEncounterParam(encounterReference),
        );
        setIntItem(response);
        if (originalSurgicalHistory && !response.surgicalHistory)
          refreshProceduresList();
      }

      updateHistoryItem(response);
      refreshPatientHistory();

      if (intItem.status === "entered-in-error") {
        try {
          removeHistoryItem();
        } catch (error) {
          console.error(error);
        }
      }

      // can be changed to await
      setEncounterToInProgress();

      setNotification({
        variant: "success",
        message: "Successfully saved the patient's surgery.",
      });
      setSaved(true);
    } catch (e) {
      console.error(
        "An error occured while saving the patient's sospitalization. Please try again.",
      );
      setNotification({
        variant: "error",
        message:
          "An error occured while saving patient's surgery. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => updateItem("performer", { actor: [provider] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [provider],
  );

  return (
    <form onSubmit={savePastSurgery}>
      <Grid container spacing={3} xs={12}>
        <Grid item xs={12} style={{ paddingTop: 10 }}>
          <Grid
            container
            alignContent="center"
            alignItems="center"
            direction="row"
          >
            <Grid item xs={10}>
              {!isServiceProvidersFreeText ? (
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    select
                    value={get(intItem, "serviceProvider.identifier") || ""}
                    disabled={disabled}
                    onChange={(e) => {
                      const id = e.target.value;
                      const opt = servProviders.find(
                        (o) => o.identifier === id,
                      );
                      updateItem("serviceProvider", opt ?? null);
                      setSaved(false);
                    }}
                    label="Hospital/Service Provider"
                    InputLabelProps={{ shrink: true }}
                  >
                    {servProviders.map((p) => (
                      <MenuItem value={p.identifier} key={p.identifier}>
                        {p.display}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="Hospital/Service Provider"
                  variant="outlined"
                  value={get(intItem, "serviceProvider.display") || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val || val === "") {
                      updateItem("serviceProvider", blankServiceProviders);
                    } else {
                      updateItem(
                        "serviceProvider",
                        createReference(
                          "Organization",
                          createSlug(
                            val.toLowerCase().replaceAll(" ", "-"),
                            "other",
                          ),
                          val,
                        ),
                      );
                    }
                    setSaved(false);
                  }}
                />
              )}
            </Grid>
            <Grid item xs={2} pl={2}>
              <FormControlLabel
                label="Other"
                disabled={disabled}
                control={
                  <Checkbox
                    checked={isServiceProvidersFreeText}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setIsServiceProvidersFreeText(checked);
                      if (checked)
                        updateItem("serviceProvider", blankServiceProviders);
                      else updateItem("serviceProvider", null);
                    }}
                  />
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid
            container
            alignContent="center"
            alignItems="center"
            direction="row"
          >
            <Grid item xs={10}>
              <FormControl variant="outlined" fullWidth>
                {!isProviderFreeText ? (
                  <Autocomplete
                    value={provider}
                    disableClearable={loading}
                    renderInput={(params) => (
                      <TextField
                        {...{
                          ...params,
                          ...(loading && {
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CircularProgress size={20} thickness={6} />
                                </InputAdornment>
                              ),
                            },
                          }),
                        }}
                        label="Practitioner"
                      />
                    )}
                    disabled={disabled}
                    options={providerReferences}
                    onChange={(e, v) => {
                      setProvider(v);
                    }}
                    onInputChange={(e, v) => {
                      loadSurgeons(v);
                    }}
                    getOptionLabel={(option) => option.display}
                  />
                ) : (
                  <TextField
                    value={provider?.display || ""}
                    disabled={disabled}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val || val === "") {
                        setProvider(blankPractitioner);
                      } else {
                        setProvider(
                          createReference(
                            "Practitioner",
                            createSlug(
                              val.toLowerCase().replaceAll(" ", "-"),
                              "other",
                            ),
                            val,
                          ),
                        );
                      }
                    }}
                    label="Surgeon"
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={2} pl={2}>
              <FormControlLabel
                label="Other"
                disabled={disabled}
                control={
                  <Checkbox
                    checked={isProviderFreeText}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setIsProviderFreeText(checked);
                      if (checked) setProvider(blankPractitioner);
                      else setProvider(null);
                    }}
                  />
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <TypeAheadInput
            freeOptions
            required
            optionType="snomed"
            subset={["procedure"]}
            value={get(intItem, "code") || []}
            variant="outlined"
            disabled={disabled}
            onChange={(val) => {
              if (val) {
                const code = createCodeableConcept(
                  CodingSystems.PRACTICE_CODES,
                  val.snomedCID,
                  val.snomedFSN,
                );
                updateItem("code", code);
              } else {
                updateItem("code", null);
              }
            }}
            getValueLabel={(v) => get(v, "text")}
            fieldLabel="Procedure Performed"
            getOptionLabel={(o) => o?.snomedFSN}
            size="medium"
          />
        </Grid>
        {intItem.hasOwnProperty("encounter") &&
          get(intItem, "addedFromInterventions") && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={get(intItem, "surgicalHistory")}
                    onChange={(e) => {
                      updateItem("surgicalHistory", e.target.checked);
                      setSaved(false);
                    }}
                    name="surgicalHistory"
                    color="primary"
                  />
                }
                label="Add to Surgical History"
              />
            </Grid>
          )}
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid container>
                <FormControl variant="outlined" fullWidth>
                  <TypeAheadInput
                    freeOptions
                    isMultiple
                    optionType="body-site"
                    value={intItem?.bodySite}
                    disabled={disabled}
                    variant="outlined"
                    onChange={(val) => {
                      if (val) {
                        const { bodySites = [] } = intItem;

                        const newSites = val.map((v) =>
                          createCodeableConcept(
                            CodingSystems.APPROACH_SITE_CODES,
                            v.code,
                            v.display,
                          ),
                        );

                        updateItem(
                          "bodySite",
                          unionBy(bodySites, newSites, "coding[0].code"),
                        );
                      } else {
                        updateItem("bodySite", null);
                      }
                      setSaved(false);
                    }}
                    getValueLabel={(v) => get(v, "coding[0].display")}
                    fieldLabel="Site of procedure"
                    getOptionLabel={(option) => option.display}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TypeAheadInput
            freeOptions
            optionType="snomed"
            subset={["disorder", "finding", "event", "situation"]}
            value={get(intItem, "reasonReference") || []}
            variant="outlined"
            disabled={disabled}
            onChange={(val) => {
              if (val) {
                const references = val.map((v) =>
                  createReference(v.type, v.snomedCID, v.snomedFSN),
                );
                updateItem("reasonReference", references);
              } else {
                updateItem("reasonReference", null);
              }
              setSaved(false);
            }}
            isMultiple
            getValueLabel={(v) => get(v, "display")}
            fieldLabel="Reasons for procedure"
            getOptionLabel={(o) => o?.snomedFSN}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <TypeAheadInput
            freeOptions
            optionType="snomed"
            subset={["disorder", "finding", "event", "situation"]}
            value={get(intItem, "complication") || []}
            variant="outlined"
            disabled={disabled}
            onChange={(val) => {
              if (val) {
                const references = val.map((v) =>
                  createCodeableConcept(
                    CodingSystems.CONDITION_CODE,
                    v.snomedCID,
                    v.snomedFSN,
                  ),
                );
                updateItem("complication", references);
              } else {
                updateItem("complication", null);
              }
              setSaved(false);
            }}
            isMultiple
            getValueLabel={(v) => get(v, "text")}
            fieldLabel="Complication(s)"
            getOptionLabel={(o) => o?.snomedFSN}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <DateOptions
            groupLabel="Surgery Date"
            groupLabelSize={3}
            disabled={disabled}
            date={intItem?.performedPeriod?.start || null}
            onDateChange={(date) => {
              let { performedPeriod } = intItem || {};
              if (performedPeriod) {
                performedPeriod.start = date;
              } else {
                performedPeriod = { start: date };
              }
              updateItem("performedPeriod", performedPeriod);
              setSaved(false);
            }}
            onValueTypeChange={(valueType) => {
              updateItem("performedPeriod.extension[0].valueCode", valueType);
            }}
            valueType={
              get(intItem, "performedPeriod.extension[0].valueCode") || null
            }
            maxDate={new Date()}
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <FormControl variant="outlined" fullWidth>
            <TextField
              select
              value={get(intItem, "status")}
              disabled={disabled}
              onChange={(e) => {
                updateItem("status", e.target.value);
                setSaved(false);
              }}
              label="Status"
              InputLabelProps={{ shrink: true }}
            >
              {procedureStatusCodes
                .filter((e) => e.code !== "entered-in-error")
                .map((c) => (
                  <MenuItem value={c.code} key={c.code}>
                    {c.display}
                  </MenuItem>
                ))}
            </TextField>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={6}>
          <FormControl variant="outlined" fullWidth>
            <TextField
              select
              value={get(intItem, "outcome.coding[0].code")}
              disabled={disabled}
              onChange={(e) => {
                const outcome = find(procedureOutcomeCodes, {
                  code: e.target.value,
                });
                updateItem(
                  "outcome",
                  createCodeableConcept(
                    CodingSystems.PROCEDURE_OUTCOMES,
                    outcome.code,
                    outcome.display,
                  ),
                );
                setSaved(false);
              }}
              label="Outcome"
              InputLabelProps={{ shrink: true }}
            >
              {procedureOutcomeCodes.map((c) => (
                <MenuItem value={c.code} key={c.code}>
                  {c.display}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <NotesInput
            value={intItem?.note}
            disabled={disabled}
            label="Comments/Notes"
            onChange={(notes) => {
              updateItem("note", notes);
              setSaved(false);
            }}
            placeholderText="Enter comments/notes about procedure."
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" sx={{ gap: 1 }}>
            <Button
              color="secondary"
              variant="outlined"
              size="small"
              disabled={intItem?.id === undefined || disabled}
              onClick={onDelete}
            >
              Delete
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="small"
              disabled={disabled || isDictationActive}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>
      <ComponentLoader loading={loading} />
    </form>
  );
};

export default SurgicalHistoryForm;

// if object contain encounter then show checkbox
